import React, { useState, useEffect, useRef, useCallback } from 'react'
import useMountEffect from '../../../hooks/useMountEffect'
import { DEFAULT_LARGE_TRANSITION_DURATION_MS } from '../../../constants/statics'

const WAIT_FOR_THINGS_TO_SETTLE = 500

interface VerticalExpanderProps {
  children: React.ReactNode
  maxHeight?: number
  transitionDurationMs?: number
}

const VerticalExpander: React.FC<VerticalExpanderProps> = ({
  children,
  maxHeight,
  transitionDurationMs = DEFAULT_LARGE_TRANSITION_DURATION_MS as number,
}) => {
  /**
   * Need both a state and a ref to keep track of the height of the content.  The ref is used to carry the value
   * through child component updates, while the state is used to trigger the transition effect.
   */
  const [height, _setHeight] = useState<number>(0)
  const [initialLoadComplete, setInitialLoadComplete] = useState<boolean>(false)
  const heightRef = useRef<number>(0)
  const contentRef = useRef<HTMLDivElement | null>(null)
  const observerRef = useRef<MutationObserver | null>(null)
  const resizeObserverRef = useRef<ResizeObserver | null>(null)

  const setHeight = useCallback((newHeight: number) => {
    heightRef.current = newHeight
    _setHeight(newHeight)
  }, [])

  const getConstrainedHeight = useCallback(() => {
    return maxHeight && heightRef.current > maxHeight
      ? maxHeight
      : heightRef.current
  }, [maxHeight])

  /**
   * This is mainly for initial load which sometimes needs one more reflow to get the content properly rendered.
   */
  const handleTransitionEnd = () => {
    requestAnimationFrame(() => {
      if (contentRef.current) {
        contentRef.current.style.height = `${getConstrainedHeight()}px`
        setTimeout(() => {
          if (contentRef.current) {
            contentRef.current.style.height = ''
          }
          setInitialLoadComplete(true)
        }, WAIT_FOR_THINGS_TO_SETTLE)
      }
    })
  }

  useMountEffect(() => {
    setTimeout(() => {
      handleTransitionEnd()
    }, transitionDurationMs + WAIT_FOR_THINGS_TO_SETTLE)
  })

  useEffect(() => {
    if (contentRef.current) {
      // initial height calculation
      const initialHeight = contentRef.current.scrollHeight
      setHeight(initialHeight)

      // MutationObserver for changes in children layout
      observerRef.current = new MutationObserver(() => {
        if (contentRef.current) {
          const targetHeight = contentRef.current.scrollHeight
          if (heightRef.current !== targetHeight) {
            setHeight(targetHeight)
          }
        }
      })
      observerRef.current.observe(contentRef.current, {
        childList: true,
        subtree: true,
        attributes: true,
        characterData: true,
      })

      // ResizeObserver for resize detection
      resizeObserverRef.current = new ResizeObserver(entries => {
        for (const entry of entries) {
          const targetHeight = entry.target.scrollHeight
          if (heightRef.current !== targetHeight) {
            setHeight(targetHeight)
          }
        }
      })
      resizeObserverRef.current.observe(contentRef.current)
    }

    return () => {
      observerRef?.current?.disconnect()
      resizeObserverRef?.current?.disconnect()
    }
  }, [children, setHeight])

  /**
   * Transition effect for height change.  For the initial load, the transition is a little faster.
   */
  const transition = `height ${
    initialLoadComplete ? transitionDurationMs : transitionDurationMs / 2
  }ms cubic-bezier(0.5, -.1, 0.5, 1.2)` // a little bounce at the end

  return (
    <div
      style={{
        overflow: maxHeight && height > maxHeight ? 'auto' : 'hidden',
        height: `${getConstrainedHeight()}px`,
        transition,
        maxHeight: maxHeight ? `${maxHeight}px` : 'none',
      }}
    >
      <div ref={contentRef}>{children}</div>
    </div>
  )
}

export default VerticalExpander
